import MoreOrLess, {
	type OnToggleParams,
} from '@xo-union/tk-component-more-or-less';
import { useAtomValue } from 'jotai';
import React, { type FC, memo, useCallback, useMemo } from 'react';
import { searchTextAtom } from '../../../../../../components/reviews/components/search-sort-filter/atoms';
import type VendorAnalytics from '../../../../../../decorators/vendorAnalytics';
import ACTIONS from '../../constants';
import { useTrackReviewToggle } from './hooks/useTrackReviewToggle';
import Styles from './reviewBody.scss';

interface ContentProps {
	content: string;
	searchText?: string;
}

const Content: FC<ContentProps> = ({ content, searchText }) => {
	const result = useMemo(() => {
		if (searchText) {
			if (!searchText || !searchText.trim()) return content;
			const regEx = new RegExp(`(${searchText})`, 'gi');
			return (
				<div className={Styles.reviewContent}>
					{content.split(regEx).map((text, index) => {
						const key = `${text}-${index}`;
						if (regEx.test(text))
							return (
								<mark key={key} className={Styles.highlighted}>
									{text}
								</mark>
							);
						return <span key={key}>{text}</span>;
					})}
				</div>
			);
		}

		return content;
	}, [content, searchText]);

	return <>{result}</>;
};

interface ReviewContentProps {
	content: string;
	searchText?: string;
	onToggle: (params: OnToggleParams) => void;
}

const ReviewContent: FC<ReviewContentProps> = memo(
	({ content, searchText, onToggle }) => {
		return searchText ? (
			<div
				className={Styles.readMoreContainer}
				data-testid="read-more-container"
			>
				<Content content={content} searchText={searchText} />
			</div>
		) : (
			<div
				className={Styles.readMoreContainer}
				data-testid="read-more-container"
			>
				<div data-testid="read-more-content">
					<MoreOrLess
						content={content}
						charLimit={ACTIONS.TK_REVIEW_MORE_OR_LESS_CHAR_LIMIT}
						onToggle={onToggle}
						className={Styles.reviewContent}
					/>
				</div>
			</div>
		);
	},
);

interface Props {
	content: string;
	vendorAnalytics?: VendorAnalytics;
}

export const ReviewBody: FC<Props> = ({ content, vendorAnalytics }) => {
	const trackReviewToggle = useTrackReviewToggle(vendorAnalytics);
	const searchText = useAtomValue(searchTextAtom);

	const onToggle = useCallback(
		(params: OnToggleParams) => {
			trackReviewToggle({ isTruncated: params.isTruncated });
		},
		[trackReviewToggle],
	);

	return (
		<ReviewContent
			content={content}
			searchText={searchText}
			onToggle={onToggle}
		/>
	);
};
